import React from 'react';
import './Tour.css';
import Tour, { TourStep } from './Tour';
import Monster from '../model/Monster';
import ninjaImg from '../dndimages/Ninja.png';
import searchImg from '../dndimages/search.png';
import spellbookImg from '../dndimages/spellbook.png';
import trashcanImg from '../dndimages/trashcan.png';
import upImg from '../dndimages/up-icon.png';
import downImg from '../dndimages/down-icon.png';
import Attributes from '../model/Attributes';

interface TourGuideProps {
    currentStepIndex: number;
    selectedMonsterId: string;
    mon: Monster;
    openBlock: string;
    openIndex: number;
    searchOpen: boolean;
    spellbookOpen: boolean;
    showAll: boolean;
    tourState: string;
    searchMinCr: number | null;
    searchMaxCr: number | null;
    searchKeywords: string;
    searchSelectedEnvironments: string[];
    searchSortBy: { id: string; desc: boolean }[];
    setCurrentStepIndex: (step: number) => void;
    onClose: () => void;
}

const TourGuide: React.FC<TourGuideProps> = ({ currentStepIndex, selectedMonsterId, mon, openBlock, openIndex, searchOpen,
                                               spellbookOpen, showAll, tourState, searchMinCr, searchMaxCr, searchKeywords,
                                               searchSelectedEnvironments, searchSortBy, setCurrentStepIndex, onClose }) => {

    const hiddenButtonStyle = { border: '1px gray solid', fontSize: '10px', fontStyle: 'normal', fontWeight: '400', backgroundColor: '#FF000020', borderRadius: '5px' };
    const tourSteps: TourStep[] = [{
        highlightElement: '#mainTitle',
        title: 'Welcome to Monster Forge!',
        scrollIntoView: true,
        description: <div>Let's take a quick look at the features of the Monster Forge.</div>,
        position: 'bottom'
    }, {
        highlightElement: '#monster-selector',
        title: 'Monster Selector',
        scrollIntoView: true,
        description: <div>This lets you load monsters from many different source books.
            <br/>The first time you open this page, you may see a message like 'Loaded 900 of 3207 Monster Names...'.
            This indicates that your browser is still loading data from <a target="_blank" href="https://open5e.com/" rel="noreferrer">open5e.com</a>.
            Once this data is loaded, it remains cached on your machine, and will only re-load when data updates exist.
            While the data is being loaded, you can access the monster that have been loaded (generally in alphabetic order).
            So if a monster you're looking for isn't shown yet, wait until the loading message completed.
            <div className='user-tour-action'>Type 'Assassin' in the select box, and then select the 'Assassin' option under the '5e&nbsp;Core&nbsp;Rules' section.</div></div>,
        testToContinue: () => {
            return selectedMonsterId == 'assassin';
        },
        position: 'bottom'
    }, {
        highlightElement: '#monsterGridSearch',
        title: 'Monster Grid Search',
        scrollIntoView: true,
        description: <div>This opens an advanced search dialog that lets you search for a monster by its CR level, environments and by keywords or phrases.
            This dialog uses all the monster data that is currently being loaded in the background.
            We'll come back to this dialog near the end of this tour when the monsters should have been loaded.
            <div className='user-tour-action'>Click <button className='fake-button'>Next</button>.</div>
            </div>,
        position: 'bottom'
    }, {
        highlightElement: '#stat-block-wrapper',
        title: 'Monster Content',
        scrollIntoView: false,
        description: (<div>The 'Assassin' monster is now loaded into this content.
            Almost everything in this section can be clicked and modified.
            As you mouse over an editable section, you'll see the mouse pointer change, and the content will become highlighted.<br /><br />
            <div className='user-tour-action'>Click on the number under the 'DEX' attribute and change its value up and down.
            <br/><br/>Notice how many elements change:
            <ul>
                <li>Armor Class</li>
                <li>DEX Saving Throw</li>
                <li>DEX-based skill values</li>
                <li>To-hit and damage rolls in the weapon descriptions</li>
            </ul>
            Click <button className='fake-button'>Next</button>.</div>
            </div>),
        position: 'left'
    }, {
        highlightElement: '#stat-block-wrapper',
        title: 'Monster Content',
        scrollIntoView: false,
        description: (<div>Many controls provide you a list of options<br />
            <div className='user-tour-action'>Open the 'Damage Resistances' control by clicking the word 'poison' next to the 'Damage Resistances' label and check the 'psychic' resistance.</div></div>),
        testToContinue: () => {
            return mon.damage_resistances.includes('psychic');
        },
        position: 'left'
    }, {
        highlightElement: '#stat-block-wrapper',
        title: 'Monster Content',
        scrollIntoView: false,
        description: (<div>Good job!
            <br/>Notice how the displayed content changes in real time as you modify elements in the control.
            <div className='user-tour-action'>Click off the control to close it.</div></div>),
        testToContinue: () => {
            return openIndex === -1
        },
        position: 'left'
    }, {
        highlightElement: '#showEmptyBlocks',
        title: 'Hidden Empty Blocks',
        scrollIntoView: true,
        description: (<div>
            The monster sheet only shows fields that have values.
            If you want to edit a field that isn't being shown, such as a Condition Immunity or a Bonus Action, you have to show the empty blocks.
            <div className='user-tour-action'>Click the 'Show Empty Blocks' checkbox.</div>
        </div>),
        testToContinue: () => {
            return showAll;
        },
        position: 'right'
    }, {
        highlightElement: '#stat-block-wrapper',
        title: 'Adding Damage Immunity',
        scrollIntoView: true,
        description: (<div>
            Now all fields are visible, even the empty ones. Let's add a Damage Immunity.<br />
            <div className='user-tour-action'>Open the 'Damage Immunities' control and check the 'necrotic' checkbox.<br/>Then close that dialog.</div></div>),
        testToContinue: () => {
            return mon.damage_immunities.includes('necrotic') && openIndex === -1;
        },
        position: 'left'
    }, {
        highlightElement: '#showEmptyBlocks',
        title: 'Hidden Empty Blocks',
        scrollIntoView: false,
        description: (<div>
            While you have the 'Show Empty Blocks' checkbox checked, look around at the other available sections that are empty, and thus normally hidden.
            Also notice the <label style={hiddenButtonStyle}>Add ---</label> button at the bottom of each section.
            This is also hidden, but when you mouse over that button, (even when its hidden, so long as the section it's in isn't hidden), it will show up and can be clicked.
            <br/>Since you are done adding the condition immunity, you should return to the normal mode.
            <div className='user-tour-action'>Uncheck the 'Show Empty Blocks' checkbox.</div>
        </div>),
        testToContinue: () => {
            return !showAll
        },
        position: 'right'
    }, {
        highlightElement: '#stat-block-wrapper',
        title: 'Add Action',
        scrollIntoView: false,
        description: (<div>Now, go find that button at the bottom of 'Actions' section.
            <div className='user-tour-action'>Click on the <label style={hiddenButtonStyle}>Add Action</label> button at the bottom of the 'Actions' section.</div>
        </div>),
        testToContinue: () => {
            return openBlock === 'Actions' && openIndex >= 3
        },
        position: 'left'
    }, {
        highlightElement: '#stat-block-wrapper',
        positionNextToElement: '#abilityPopup',
        title: 'Edit Action',
        scrollIntoView: false,
        description: (<div>
            In this edit view are several controls:
            <table>
                <tbody>
                    <tr><td style={{ textAlign: 'center' }}><img src={upImg} style={{ width: '16px', height: '16px' }} alt="Move Up" /> +
                        <img src={downImg} style={{ width: '16px', height: '16px' }} alt="Move Down" /></td><td> Moves this ability up or down within the 'Actions'' section</td></tr>
                    <tr><td style={{ textAlign: 'center' }}><img src={searchImg} style={{ width: '16px', height: '16px' }} alt="Search" /></td><td> Opens the search dialog</td></tr>
                    <tr><td style={{ textAlign: 'center' }}><img src={trashcanImg} style={{ width: '16px', height: '16px' }} alt="Delete" /></td><td> Deletes this ability</td></tr>
                    <tr><td><button className="small-button">?&nbsp;Format&nbsp;Help</button></td><td>Opens format help</td></tr>
                    <tr><td colSpan={2}>
                        And the dropdown (showing <span className="block-header-font" style={{ backgroundColor: 'oldlace', fontSize: 'small' }}>Actions</span>)
                        allow you to move this ability into a different section, such as the 'Traits' or 'Spellcasting' section</td></tr>
                </tbody>
            </table>
            <div className='user-tour-action'>Click on the <img src={searchImg} style={{ width: '16px', height: '16px' }} alt="Search" /> button to open the search dialog.</div>
        </div>),
        testToContinue: () => {
            return searchOpen
        },
        position: 'right'
    }, {
        highlightElement: '#stat-block-wrapper',
        positionNextToElement: '#abilityPopup',
        title: 'Search Action',
        scrollIntoView: false,
        description: (<div>
            This search controls let you find common abilities. The content shown is filtered down to the abilities specific to the section you're in.
            Since you came here from the 'Actions' section, only monster's abilities that use an action are listed. If you came in from the 'Traits' section,
            the available set would include other options such as 'Evasion' or 'Berserk'.
            <br />After you select an entry, the description section lets you preview the ability before you press the <button className='fake-button'>Select</button> button.
            <div className='user-tour-action'>Type 'dagger' in the search bar at the top (or just scroll down)
                <br />Select the 'Dagger (DEX)' entry.
                <br />Then click <button className='fake-button'>Select</button>.</div>
        </div>),
        testToContinue: () => {
            return !searchOpen && mon.actions[3].name === 'Dagger'
        },
        position: 'right'
    }, {
        highlightElement: '#stat-block-wrapper',
        positionNextToElement: '#abilityPopup',
        title: 'Dagger Details',
        scrollIntoView: false,
        description: (<div>
            After selecting 'Dagger (DEX)', you notice that the Name is populated with 'Dagger', and the description contain a markdown of the text that
            appears on the monster sheet, just above the dialog.<br />
            Notice how some content such as '[DEX ATK]' is replaced with '+{(Attributes.getBonus(mon.attributes.dexterity) + mon.proficiencyBonus)}',
            and '[DEX 1d4]' is replaced with '{2+Attributes.getBonus(mon.attributes.dexterity)} (1d4 + {Attributes.getBonus(mon.attributes.dexterity)})', matching the monster's DEX and proficiency bonus.
            Click on the <button className="small-button">?&nbsp;Format&nbsp;Help</button> for more details.
            <div className='user-tour-action'>Click off the control to close it.</div>
        </div>),
        testToContinue: () => {
            return openIndex === -1
        },
        position: 'right'
    }, {
        highlightElement: '#monster-alias-table',
        title: 'Short/Plural Name selectors',
        scrollIntoView: true,
        description: (<div>
            Most of the markdown text provided includes [MON] or [MONS] as a placeholder for the monster's name.
            These controls let you specify the name used in that text.
            By default, [MON] will be the monster's name and for the plural form ([MONS]), it will add an 's' at the end.
            This is very useful when you have a long monster name, such as 'Ancient Red Dragon', that you only want to show as 'dragon' in most text.
            <div className='user-tour-action'>Click <button className='fake-button'>Next</button>.</div>
        </div>),
        position: 'right'
    }, {
        highlightElement: '#layoutControls',
        title: 'Layout Controls',
        scrollIntoView: true,
        description: (<div>
            These elements control the overall layout of the page.
            When displaying in two columns, you have the ability to control where the breaking point is using the <span style={{border: '1px solid black', borderRadius: '4px', fontWeight: '600', backgroundColor: 'oldlace'}}>⮰</span> and <span style={{border: '1px solid black', borderRadius: '4px', fontWeight: '600', backgroundColor: 'oldlace'}}>⮳</span> buttons.
            <div className='user-tour-action'>Click <span style={{border: '1px solid black', borderRadius: '4px'}}>⮰</span> twice to move the 'Evasion' and 'Sneak Attack' abilities from the right column to the left column.</div>
        </div>),
        testToContinue: () => {
            return mon.splitPoint == 4;
        },
        position: 'right'
    }, {
        highlightElement: '#stat-block-wrapper',
        title: 'Adding an Image',
        scrollIntoView: true,
        description: (<div>
            Now that you've made enough room, you can add an image to this monster.
            You can load an image from a file using the <button className='fake-button'>Load Image</button> button at the bottom,
            or you can drag-and-drop an image from another source
            (such as a web page on another tab).
            <br />NOTE: Use PNG images with transparent portions for best results.
            <div className='user-tour-action'>Drag this image of ninja onto the monster area and release it.
                <br /><img src={ninjaImg} alt="ninja" width='48px' style={{ paddingLeft: '100px' }} />
            </div>
        </div>),
        testToContinue: () => {
            return !!mon.image?.src
        },
        position: 'right'
    }, {
        highlightElement: '#bottomEdgeControls',
        title: 'Making Room for the Image',
        scrollIntoView: true,
        description: (<div>
            You see that the image is now shown, but it's too big. You can add some more room for it by lowering the bottom edge.
            <div className='user-tour-action'>click the <button className="move-column-button">⏷</button> button twice.</div>
        </div>),
        testToContinue: () => {
            return mon.extraWhitespace == 2;
        },
        position: 'right'
    }, {
        highlightElement: '#stat-block-wrapper',
        title: 'Positioning the Image',
        scrollIntoView: true,
        description: (<div>
            As long as the image is not marked as 'Behind' the text, you can drag the image around the screen anywhere you want.
            <div className='user-tour-action'>
                Grab and drag the image, positioning it at the bottom of the sheet. Then click <button className='fake-button'>Next</button>.
            </div>
        </div>),
        position: 'right'
    }, {
        highlightElement: '#imageControls',
        title: 'Resizing the Image',
        scrollIntoView: false,
        description: (<div>
            These control let us manage the image even more. You can control the size, opacity, rotation, flip it horizontally, place it behind the text,
            and control whether it gets clipped by the monster sheet's edges.
            <div className='user-tour-action'>
                Play with the image controls to understand their effects.
                <br/>Then click <button className='fake-button'>Next</button>.
            </div>
        </div>),
        position: 'right'
    }, {
        highlightElement: '#loggedInFeatures',
        title: 'Logged-in Feature',
        scrollIntoView: true,
        description: (<div>
            The features in this block require you to log in before you can access them.
            Click the 'login' button in the top right of the screen to log in using either a verified email address, or a google account.
            <div className='user-tour-action'>
                Click <button className='fake-button'>Next</button>.
            </div>
        </div>),
        position: 'right'
    }, {
        highlightElement: '#myMonsterSection',
        title: 'My Monsters Feature',
        scrollIntoView: true,
        description: (<div>
            The <button className='fake-button'>Add</button> button adds the currently shown monster into your own personal monsters list.
            If you already have a monster with the same name in your monsters list, this button becomes <button className='fake-button'>Update</button>,
            and the <button className='fake-button'>Remove</button> button becomes enabled.
            These monsters are stored in the cloud with your account, and are listed in the 'My Monsters' section of the monster select bar.
            They are available across any device accessing your account.
            <br/>For the first 30 days, all new accounts are able to store up to 5 monsters in your account for free.
            <br/>After 30 days, you'll need to select a subscription plan to store monsters in the cloud. Subscriptions allow you to store many monsters online for a small annual fee.
            <br/>Any monsters stored in your account will be deleted if you don't move to a subscription plan after 30 days.
            <div className='user-tour-action'>
                Click <button className='fake-button'>Next</button>.
            </div>
        </div>),
        position: 'bottom'
    }, {
        highlightElement: '#pcNpcWizardSection',
        title: 'Create PC/NPC Feature',
        scrollIntoView: false,
        description: (<div>
            When you are logged in and have any level of subscription (or within the first 30 days), you can also access this PC/NPC creation wizard.
            It's ideal for making a quick build of a PC or an NPC, giving all the features defined by the open5e definitions of classes.
            All of the features for each class are listed in the monster sheet once you complete the wizard.
            After you've made an NPC, you'll likely want to go through the features and remove (or alter) those that are not relevant for an NPC.
            <div className='user-tour-action'>
                Click <button className='fake-button'>Next</button>.
            </div>
        </div>),
        position: 'bottom'
    }, {
        highlightElement: '#monsterGridSearch',
        title: 'Monster Grid Search',
        scrollIntoView: true,
        description: <div>Now that we've given some time for the monsters to load, let's take a look at the advanced search dialog.
            <div className='user-tour-action'>Open the monster search dialog by clicking the <img src={searchImg} style={{ width: '16px', cursor: 'none' }}/></div>
            </div>,
        testToContinue: () => {
            return tourState === 'monsterGridOpen';
        },
        position: 'bottom'
    }, {
        highlightElement: '#AdvancedMonsterGrid',
        alternateElement: '#monsterGridSearch',
        positionNextToElement: '#MonsterGrid',
        title: 'Monster Grid Search',
        scrollIntoView: true,
        description: <div>You can search for monsters based on their CR rating, enivronment and key words or phrases.
            Suppose we want to find monsters that have a breath weapon. 
            <div className='user-tour-action'>Type 'breath weapon' (without the quotes) in the keywords.</div>
            </div>,
        testToContinue: () => {
            return (tourState === 'monsterGridOpen') &&
                    !!searchKeywords &&
                    (searchKeywords.toLowerCase().trim() === 'breath weapon');
        },
        position: 'left'
    }, {
        highlightElement: '#AdvancedMonsterGrid',
        alternateElement: '#monsterGridSearch',
        positionNextToElement: '#MonsterGrid',
        title: 'Monster Grid Search',
        scrollIntoView: true,
        description: <div>The results show monsters that have both 'breath' and 'weapon', so we get lots of results, for monster that can hold their breath, and monsters that are amphibious ('can <b>breath</b>e air and water'), etc.
            To limit our results to only those with an actual breath weapon, surround it in quotes, forcing it to match the phrase "breath weapon".
            <div className='user-tour-action'>Put quotes (' or ") around the keywords 'breath weapon'.</div>
            </div>,
        testToContinue: () => {
            return (tourState === 'monsterGridOpen') && 
                    !!searchKeywords && 
                    (searchKeywords.toLowerCase().trim() === '"breath weapon"' || searchKeywords.toLowerCase().trim() === '\'breath weapon\'');
        },
        position: 'left'
    }, {
        highlightElement: '#AdvancedMonsterGrid',
        alternateElement: '#monsterGridSearch',
        positionNextToElement: '#MonsterGrid',
        title: 'Monster Grid Search',
        scrollIntoView: true,
        description: <div>The results show monsters that have both 'breath' and 'weapon', so we get lots of results, about 730 monsters.
            Many of these results are for monster that can hold their breath or are amphibious ('can <b>breath</b>e air and water') and reference a weapon, etc..
            To limit our results to only those with an actual breath weapon, surround it in quotes, forcing it to make the phrase "breath weapon".
            <div className='user-tour-action'>Put quotes (' or ") around the keywords 'breath weapon'.</div>
        </div>,
        testToContinue: () => {
            return (tourState === 'monsterGridOpen') &&
                    !!searchKeywords &&
                    (searchKeywords.toLowerCase().trim() === '"breath weapon"' || searchKeywords.toLowerCase().trim() === '\'breath weapon\'');
        },
        position: 'left'
    }, {
        highlightElement: '#AdvancedMonsterGrid',
        alternateElement: '#monsterGridSearch',
        positionNextToElement: '#MonsterGrid',
        title: 'Monster Grid Search',
        scrollIntoView: true,
        description: <div>Now the results size is much more reasonable, a little over 100 monsters.
            The majority of these results are dragons. If we want to exclude dragons from these results, add the term "-dragon" (without the quotes).
            This will remove any monster that contains the word 'dragon' in its description anywhere.
            <div className='user-tour-action'>Add ' -dragon' to the keywords ('"breath weapon" -dragon').</div>
            </div>,
        testToContinue: () => {
            return (tourState === 'monsterGridOpen') &&
                    !!searchKeywords &&
                    (searchKeywords.toLowerCase().trim() === '"breath weapon" -dragon' || searchKeywords.toLowerCase().trim() === '\'breath weapon\' -dragon');
        },
        position: 'left'
    }, {
        highlightElement: '#AdvancedMonsterGrid',
        alternateElement: '#monsterGridSearch',
        positionNextToElement: '#MonsterGrid',
        title: 'Monster Grid Search',
        scrollIntoView: true,
        description: <div>Now the results size is only 5 monsters, so it's likely that the majority of monsters with breath weapons reference the word 'dragon' in their description, even if they are not themselves a dragon.
            So lets remove the ' -dragon' from the keywords to get back to the ~100 monsters, and then lets sort on the 'Type' column.
            <div className='user-tour-action'>Remove ' -dragon' from the keywords.</div>
            </div>,
        testToContinue: () => {
            return (tourState === 'monsterGridOpen') &&
                    !!searchKeywords &&
                    (searchKeywords.toLowerCase().trim() === '"breath weapon"' || searchKeywords.toLowerCase().trim() === '\'breath weapon\'');
        },
        position: 'left'
    }, {
        highlightElement: '#AdvancedMonsterGrid',
        alternateElement: '#monsterGridSearch',
        positionNextToElement: '#MonsterGrid',
        title: 'Monster Grid Search',
        scrollIntoView: true,
        description: <div>Now the results size is only 5 monsters, so it's likely that the majority of monsters with breath weapons reference the word 'dragon' in their description, even if they are not themselves a dragon.
            So lets remove the ' -dragon' from the keywords to get back to the ~100 monsters, and then lets sort on the 'Type' column.
            <div className='user-tour-action'>Now, click the 'Type' column header to sort that column.</div>
            </div>,
        testToContinue: () => {
            return (tourState === 'monsterGridOpen') &&
                    !!searchKeywords &&
                    (searchKeywords.toLowerCase().trim() === '"breath weapon"' || searchKeywords.toLowerCase().trim() === '\'breath weapon\'') &&
                    searchSortBy.length === 1 && searchSortBy[0].id === 'type';
        },
        position: 'left'
    }, {
        highlightElement: '#AdvancedMonsterGrid',
        alternateElement: '#monsterGridSearch',
        positionNextToElement: '#MonsterGrid',
        title: 'Monster Grid Search',
        scrollIntoView: true,
        description: <div>Now the that we are sorting by monster type, we can scroll through the list, and use the paging to see what monsters in the list aren't actual dragons.
            On the first page we see a few monsters, and on the last page(s) we see several more.
            <div className='user-tour-action'>Clear the search keyword.
            </div>
            </div>,
        testToContinue: () => {
            return (tourState === 'monsterGridOpen') && !searchKeywords;
        },
        position: 'left'
    }, {
        highlightElement: '#AdvancedMonsterGrid',
        alternateElement: '#monsterGridSearch',
        positionNextToElement: '#MonsterGrid',
        title: 'Monster Grid Search',
        scrollIntoView: true,
        description: <div>You can refine your by the CR using the 'min CR' and 'max CR' controls in the 'CR Range' section.
            <div className='user-tour-action'>Use the CR Range section to find all monsters between CR 5 and CR 9.</div>
            </div>,
        testToContinue: () => {
            return searchMinCr === 5 && searchMaxCr === 9;
        },
        position: 'left'
    }, {
        highlightElement: '#AdvancedMonsterGrid',
        alternateElement: '#monsterGridSearch',
        positionNextToElement: '#MonsterGrid',
        title: 'Monster Grid Search',
        scrollIntoView: true,
        description: <div>You can also limit the results to show only monsters that list at least one of the environment you select from the 'Environments' control under the keywords control.
            <div className='user-tour-action'>Click on the red '--' under the keywords to open the environments control, and select 'Underdark'.</div>
            </div>,
        testToContinue: () => {
            return searchMinCr === 5 && searchMaxCr === 9 && searchSelectedEnvironments.length === 1 && searchSelectedEnvironments[0] === 'Underdark';
        },
        position: 'left'
    }, {
        highlightElement: '#AdvancedMonsterGrid',
        alternateElement: '#monsterGridSearch',
        positionNextToElement: '#MonsterGrid',
        title: 'Monster Grid Search',
        scrollIntoView: true,
        description: <div>Now close that environments control, and you'll see all the monsters with a CR of 5-9 that list 'Underdark' as one of their environments.
            <br/>It's worth noting that most sources other than 'WoTC-srd' do not list environments for their monsters.
            <div className='user-tour-action'>Find a CR level 7 monster that inhabits the Underdark. Once you find it in the list, click on its name.</div>
            </div>,
        testToContinue: () => {
            return mon.environments.includes('Underdark') && mon.cr == 7;
        },
        position: 'left'
    }, {
        highlightElement: '#showEmptyBlocks',
        title: 'Adding New Section',
        scrollIntoView: true,
        description: (<div>Good job, you found the {mon.name}!
            <br/><br/>Now lets take a look at another advanced feature: spellcasting.
            <br/>So let's give this {mon.name} some spellcasting abilities. First, you'll have to show the empty blocks.
            <div className='user-tour-action'>Click the 'Show Empty Blocks' checkbox.</div>
        </div>),
        testToContinue: () => {
            return showAll;
        },
        position: 'right'
    }, {
        highlightElement: '#stat-block-wrapper',
        positionNextToElement: '#abilityPopup',
        title: 'Add spellcasting',
        scrollIntoView: true,
        description: (<div>
            Now that you can see all the available sections, let's add some spellcasting abilities to this monster.
            If the image is on top of the <label style={hiddenButtonStyle}>Add Spellcasting</label> button, you may have to move the image in order to click the button.
            <div className='user-tour-action'>Click the <label style={hiddenButtonStyle}>Add Spellcasting</label> button in the Spellcasting section.</div>
        </div>),
        testToContinue: () => {
            return openBlock === 'Spellcasting' && openIndex === 0
        },
        position: 'right'
    }, {
        highlightElement: '#stat-block-wrapper',
        positionNextToElement: '#abilityPopup',
        title: 'Add spellcasting',
        scrollIntoView: false,
        description: (<div>
            The Spellcasting section is a little different from the other sections. You'll notice that the search icon has switch from
            <img src={searchImg} style={{ width: '16px', height: '16px' }} alt="Search" /> to <img src={spellbookImg} style={{ width: '16px', height: '16px' }} alt="Search" />
            <div className='user-tour-action'>Click the <img src={spellbookImg} style={{ width: '16px', height: '16px' }} alt="Search" /> button.</div>
        </div>),
        testToContinue: () => {
            return spellbookOpen;
        },
        position: 'right'
    }, {
        highlightElement: '#stat-block-wrapper',
        positionNextToElement: '#abilityPopup',
        title: 'Add spellcasting',
        scrollIntoView: false,
        description: (<div>
            This control lets you pick up to two classes at specified levels for spellcasting.
            In this case, you are just trying to add some wizard's spells, comprable to what a 3th level wizard could cast.
            <div className='user-tour-action'>Select 'Wizard' in the first dropdown.</div>
        </div>),
        testToContinue: () => {
            return tourState.startsWith("SpellCastingSelector: Wizard|");
        },
        position: 'right'
    }, {
        highlightElement: '#stat-block-wrapper',
        positionNextToElement: '#abilityPopup',
        title: 'Add spellcasting',
        scrollIntoView: false,
        description: (<div>
            Because you chose 'wizard', and wizard's prepared spells all must come from their spellbook, you see a section for the wizards spellbook.
            Because the level dropdown has a value of '1', you only see options to edit 1st level spells.
            The entries you choose in the spellbook will be the only available options to choose from in the 'Prepared Spells' section below (other than cantrips).
            <br />If we'd chosen another class, the spellbook section would not be shown, and the spells available to prepare would come directly from the class chosen.
            <br />NOTE: The 'Inate Caster - Int/Wis/Cha' class gives access to all spells, from all classes, using the declared casting attribute.
            <div className='user-tour-action'>Select '3' in the level selector for the primary class in the header of the 'Prepared Spells' table.</div>
        </div>),
        testToContinue: () => {
            return tourState === "SpellCastingSelector: Wizard|3|true";
        },
        position: 'right'
    }, {
        highlightElement: '#stat-block-wrapper',
        positionNextToElement: '#abilityPopup',
        title: 'Add spellcasting',
        scrollIntoView: false,
        description: (<div>
            Now that we've chosen a higher wizard level, you see additional spell level are available to both enter into our spellbook and to prepare.
            <br />The numbers in parenthesis tell you how many spells have been selected and the maximum allowed at that level.
            It's generally best to select your higher level spells first, so you don't consume any of your allowed higher level spell options
            with a lower level spell (i.e. if you choose all 10 1st level spells, you'd have no options for any 2nd level spells).
            <div className='user-tour-action'>Click on the red '--' text on the 2nd row, in the 'Spells' column of the spellbook table.</div>
        </div>),
        testToContinue: () => {
            return tourState === "SpellCastingSelector: Wizard|3|Spellbook:2";
        },
        position: 'right'
    }, {
        highlightElement: '#stat-block-wrapper',
        title: 'Select spellbook spells',
        scrollIntoView: false,
        description: (<div>
            Here are all the spells your chosen class can learn. mousing over the&nbsp;
            <svg width="10" height="10" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="black" strokeWidth="2" fill="none" />
                <text x="50%" y="55%" fontSize="16" textAnchor="middle" fontStyle="italic" fill="black" dy=".3em">i</text>
            </svg> icon to see the details about any spell.
            <div className='user-tour-action'>Choose any two spells.<br />
            </div>
        </div>),
        testToContinue: () => {
            return mon.npcData.spellbookSpellSlugsBySpellLevelPrimary[2]?.split(',').length >= 2;
        },
        position: 'right'
    }, {
        highlightElement: '#stat-block-wrapper',
        title: 'Select spellbook spells',
        scrollIntoView: false,
        description: (<div>Notice how the other options are no longer enabled.
            <div className='user-tour-action'>Click off the dialog to close that spell level.</div>
        </div>),
        testToContinue: () => {
            return tourState === "Spellbook level closed";
        },
        position: 'right'
    }, {
        highlightElement: '#stat-block-wrapper',
        positionNextToElement: '#abilityPopup',
        title: 'Add spellcasting',
        scrollIntoView: false,
        description: (<div>
            Do the same for the 1st spell level, selecting at least one spell.
            <div className='user-tour-action'>Close that dialog when you are done.</div>
        </div>),
        testToContinue: () => {
            return mon.npcData.spellbookSpellSlugsBySpellLevelPrimary[1].length > 0 &&  tourState === "Spellbook level closed";
        },
        position: 'right'
    }, {
        highlightElement: '#stat-block-wrapper',
        positionNextToElement: '#abilityPopup',
        title: 'Add spellcasting',
        scrollIntoView: false,
        description: (<div>
            Now that you've selected the spells in you book, you can click on the '--' entries in the 'Prepared Spells' section
            and choose the spells you want to prepare from your spellbook.
            <div className='user-tour-action'>Select some spells and cantrips for each level.
                <br />Then press the <button className='fake-button'>Select</button> button.
            </div>
        </div>),
        testToContinue: () => {
            return mon.spells.length > 0 && ["Wizard's Spellbook", "Prepared Spells"].includes(mon.spells[0].name);
        },
        position: 'right'
    }, {
        highlightElement: '#stat-block-wrapper',
        positionNextToElement: '#abilityPopup',
        title: 'Add Spellcasting',
        scrollIntoView: false,
        description: (<div>
            Now you see your spellbook and prepared spells in the markdown text for the 'Wizard`s Spellbook' ability.
            <br />You can also in the monster sheet how the table looks fully rendered.
            If you don't want to see the spellbook you can either manually delete that markdown text in the editor,
            or go back into the spellbook editor and change the class from 'wizard' to 'Inate Caster - Int', which gives you
            access to all spells, but doesn't require a spellbook.
            <div className='user-tour-action'>Choose how you want the spells to show, then click off the dialog to close that ability editor.</div>
        </div>),
        testToContinue: () => {
            return openIndex === -1
        },
        position: 'right'
    }, {
        highlightElement: '#showEmptyBlocks',
        title: 'Empty Blocks',
        scrollIntoView: true,
        description: (<div>
            Once you are done adding new section, you should return to the normal mode where empty blocks are not shown.
            <div className='user-tour-action'>Uncheck the 'Show Empty Blocks' checkbox.</div>
        </div>),
        testToContinue: () => {
            return !showAll
        },
        position: 'right'
    }, {
        highlightElement: '#stat-block-wrapper',
        title: 'Challenge Level',
        scrollIntoView: true,
        description: (<div>
            As you edit a monster, the 'Challenge' Rating (CR) will not update. This is a field that must be manually maintained.
            When you change the CR value of a monster, it will update the monster's Proficiency Bonus, which will in turn affect many other things, such as Skill levels and to-hit modifiers.
            <div className='user-tour-action'>Set the Challenge Rating of this monster to 9.</div>
        </div>),
        testToContinue: () => {
            return mon.cr == 9;
        },
        position: 'left'
    }, {
        highlightElement: '#DownloadMonsterButton',
        title: 'Saving your work',
        scrollIntoView: true,
        description: (<div>
            Now that you've selected, modified, or created a monster, you'll want to save it.
            You can save a few in your My Monsters list in the top right controls after you've logged in.
            But you can also save your monster using the <button className='fake-button'>Download Monster</button> button.
            This will download a file with the name of your monster, with a .json extension.
            <div className='user-tour-action'>Click <button className='fake-button'>Download Monster</button> button</div>
        </div>),
        testToContinue: () => {
            return tourState == 'monster saved';
        },
        position: 'top'
    }, {
        highlightElement: '#SaveMonsterSection',
        title: 'Saving your work',
        scrollIntoView: true,
        description: (<div>
            In addition to saving the raw monster file, you can also download a statblock image of monster as seen in most source books.
            Clicking the <button className='fake-button'>Download Image</button> button will download an image of this statblock to your download folder.
            <br/>Also, you can click the <button className='fake-button'>Print View</button> button to open a new window with a greyscale image of the statblock, suitable for printing.
            <div className='user-tour-action'>Click <button className='fake-button'>Next</button> button</div>
        </div>),
        position: 'top'
    }, {
        highlightElement: '#LoadMonsterSection',
        title: 'Loading your work',
        scrollIntoView: true,
        description: (<div>
            You can re-open any monster file you saved later using the <button className='fake-button'>Load Monster</button> button,
            or by dragging that file onto the monster sheet, just like you did with the image.
            <br/><br/>You can even load monsters from the <a style={{fontSize: 'small'}} href='https://tetra-cube.com/dnd/dnd-statblock.html' target="_blank">tetra-cube.com/dnd/dnd-statblock.html</a> statblock generator!
            <div className='user-tour-action'>Click <button className='fake-button'>Next</button> button</div>
        </div>),
        position: 'top'
    }, {
        highlightElement: '#showUseMm2024',
        title: 'Monster Manual 2024 Style',
        scrollIntoView: true,
        description: (<div>
            If you want to view a monster in the style and layout of the new Monster Manual 2024, just click the 'Use MM2024 style' checkbox.
            <div className='user-tour-action'>Click the 'Use MM2024 style' checkbox.</div>
        </div>),
        testToContinue: () => {
            return tourState == 'useMm2024';
        },
        position: 'top'
    }, {
        highlightElement: '#stat-block-wrapper',
        title: 'Monster Manual 2024 Style',
        scrollIntoView: true,
        description: (<div>
            In MM2024, initiative is a skill, but is displayed next to AC.
            <div className='user-tour-action'>Click on the initiative field</div>
        </div>),
        testToContinue: () => {
            return openBlock === 'skills';
        },
        position: 'left'
    }, {
        highlightElement: '#stat-block-wrapper',
        title: 'Monster Manual 2024 Style',
        scrollIntoView: true,
        description: (<div>
            You see this opens up the normal skills selector, same as if you'd clicked on the skills section.
            While in MM2024, this skill wont show in the normal skills list area, but if you switch back to the legacy view, you will see it with the other skills.
            <div className='user-tour-action'>Give this monster proficiency in the Initiative skill. Then close the skills selector.</div>
        </div>),
        testToContinue: () => {
            return mon.skillSet.skills['Initiative']?.level === 'proficient' && openIndex === -1;
        },
        position: 'left'
    }, {
        highlightElement: '#showEmptyBlocks',
        title: 'MM2024 Immunities',
        scrollIntoView: true,
        description: (<div>
            The Damage and Condition Immunities have been combined into one area, but are still 2 separate controls.
            First, you'll have to show the empty blocks again.
            <div className='user-tour-action'>Click the 'Show Empty Blocks' checkbox.</div>
        </div>),
        testToContinue: () => {
            return showAll;
        },
        position: 'right'
    }, {
        highlightElement: '#stat-block-wrapper',
        title: 'MM2024 Immunities',
        scrollIntoView: true,
        description: (<div>
            While the 'Show Empty Blocks' is on, you'll see both immunity sections next to each other.
            <div className='user-tour-action'>Add imminutity to the 'fire' damage type, and the 'petrified' condition.
                                              Then close both immunity controls</div>
        </div>),
        testToContinue: () => {
            return mon.damage_immunities.includes('fire') && mon.condition_immunities.includes('petrified') && openIndex === -1;
        },
        position: 'left'
    }, {
        highlightElement: '#stat-block-wrapper',
        title: 'Monster Manual 2024 Style',
        scrollIntoView: true,
        description: (<div>
            Saving throws are now adjusted by clicking on the new saving throw field, next to each attribute.
            <div className='user-tour-action'>Cick on the saving throw entry for the Strength attribute to give your monster proficiency in STR saves.</div>
        </div>),
        testToContinue: () => {
            return mon.saving_throws.toLowerCase().includes('str');
        },
        position: 'left'
    }, {
        highlightElement: '#stat-block-wrapper',
        title: 'Monster Manual 2024 Style',
        scrollIntoView: true,
        description: (<div>
            Gear is a new field when viewing in MM2024 style. 
            As you change your armor, or add/remove actions that correspond to simple weapons, this field will update.
            In fact, this is now the only place you'll see your current armor/shield on the main sheet.
            Magic items from the 'Inventory' section will also be shown here.
            <div className='user-tour-action'>Click <button className='fake-button'>Next</button> button</div>
        </div>),
        position: 'left'
    }, {
        highlightElement: '#mainTitle',
        scrollIntoView: true,
        title: 'Finished',
        description: (<div>
            That's the end of this tour.
            <br/><br/>Feel free to send feeback to about any problems, comments or suggestion!<br/><br/>
        </div>),
        position: 'bottom'
    }];

    if (currentStepIndex >= tourSteps.length) {
        return null;
    }
    return <Tour steps={tourSteps} currentStepIndex={currentStepIndex} setCurrentStepIndex={setCurrentStepIndex} onClose={onClose} />
}

export default TourGuide;